import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, MailOpen, Plus, Pencil, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

const TABS = ["Messages", "Portfolio", "Témoignages", "Tarifs", "Contenu"];

const FIELDS = {
  Portfolio: [
    { key: "titre", label: "Titre" },
    { key: "categorie", label: "Catégorie", type: "select", options: ["Portrait", "Famille", "Couple", "Événement", "Studio", "Architecture", "Paysage", "Nature et animaux"] },
    { key: "url", label: "URL de la photo", full: true },
    { key: "lien", label: "Lien externe au clic (optionnel, ex. galerie AfterShoot)", full: true },
  ],
  Témoignages: [
    { key: "titre", label: "Titre", full: true },
    { key: "texte", label: "Témoignage", type: "textarea", full: true },
    { key: "auteur", label: "Nom" },
    { key: "contexte", label: "Petit texte (ex. type de séance)" },
  ],
  Tarifs: [
    { key: "titre", label: "Titre" },
    { key: "prix", label: "Prix" },
    { key: "description", label: "Description", type: "textarea", full: true },
    { key: "details", label: "Détails", full: true },
  ],
};

const TAB_ENDPOINT = { Portfolio: "portfolio", Témoignages: "testimonials", Tarifs: "pricing" };

const CONTENT_FIELDS = [
  { key: "hero_image", label: "Photo principale (accueil)" },
  { key: "about_image", label: "Photo de Daniel (à propos)" },
  { key: "quote_image", label: "Photo de la citation (paysage)" },
  { key: "studio_image", label: "Photo d'ambiance du studio" },
  { key: "autumn_image", label: "Photo de la section automne" },
  { key: "aftershoot_url", label: "Lien galerie AfterShoot" },
  { key: "facebook_url", label: "Lien Facebook" },
  { key: "instagram_url", label: "Lien Instagram" },
  { key: "youtube_url", label: "Lien YouTube" },
  { key: "tiktok_url", label: "Lien TikTok" },
];

const inputClass = "w-full bg-[#0A0A0C] border border-[#D4AF37]/20 focus:border-[#D4AF37]/70 outline-none px-4 py-3 text-sm text-[#F8F9FA] placeholder:text-slate-600 transition-colors";

function ItemForm({ tab, initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial || {});
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSave(form);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="gold-card p-6 space-y-4" data-testid={`${TAB_ENDPOINT[tab]}-item-form`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {FIELDS[tab].map((f) => (
          <div key={f.key} className={f.full ? "sm:col-span-2" : ""}>
            <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] block mb-2">{f.label}</label>
            {f.type === "textarea" ? (
              <textarea rows={3} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                        data-testid={`form-field-${f.key}`} className={`${inputClass} resize-none`} />
            ) : f.type === "select" ? (
              <select value={form[f.key] || f.options[0]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                      data-testid={`form-field-${f.key}`} className={inputClass}>
                {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
              <input value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                     data-testid={`form-field-${f.key}`} className={inputClass} />
            )}
          </div>
        ))}
      </div>
      <div className="flex gap-3">
        <button type="submit" disabled={busy} data-testid="item-form-save-button" className="btn-gold !px-6 !py-3 disabled:opacity-50">
          {busy ? "Enregistrement…" : "Enregistrer"}
        </button>
        <button type="button" onClick={onCancel} data-testid="item-form-cancel-button" className="btn-outline-gold !px-6 !py-3">Annuler</button>
      </div>
    </form>
  );
}

function CollectionTab({ tab }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(undefined);
  const endpoint = TAB_ENDPOINT[tab];

  const load = useCallback(() => api.get(`/${endpoint}`).then(({ data }) => setItems(Array.isArray(data) ? data : [])).catch(() => {}), [endpoint]);
  useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    try {
      if (editing && editing.id) await api.put(`/admin/${endpoint}/${editing.id}`, { data: form });
      else await api.post(`/admin/${endpoint}`, { data: form });
      toast.success("Enregistré.");
      setEditing(undefined);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/${endpoint}/${id}`);
    toast.success("Supprimé.");
    load();
  };

  return (
    <div className="space-y-6" data-testid={`admin-tab-${endpoint}`}>
      {editing !== undefined ? (
        <ItemForm tab={tab} initial={editing} onSave={save} onCancel={() => setEditing(undefined)} />
      ) : (
        <button onClick={() => setEditing({})} data-testid={`add-${endpoint}-button`} className="btn-outline-gold !px-6 !py-3">
          <Plus size={14} /> Ajouter
        </button>
      )}
      <div className="space-y-3">
        {items.map((item) => (
          <div key={item.id} className="gold-card p-5 flex items-center gap-5" data-testid={`admin-item-${item.id}`}>
            {item.url && <img src={item.url} alt="" className="w-16 h-16 object-cover border border-[#D4AF37]/20 shrink-0" />}
            <div className="flex-1 min-w-0">
              <p className="font-serif text-lg text-[#F8F9FA] truncate">{item.titre || item.auteur}</p>
              <p className="text-slate-500 text-xs truncate mt-1">{item.categorie || item.prix || item.texte}</p>
            </div>
            <button onClick={() => setEditing(item)} data-testid={`edit-item-${item.id}`} className="text-[#D4AF37] hover:text-[#F3E5AB] p-2" aria-label="Modifier">
              <Pencil size={16} />
            </button>
            <button onClick={() => remove(item.id)} data-testid={`delete-item-${item.id}`} className="text-slate-500 hover:text-red-400 p-2" aria-label="Supprimer">
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function MessagesTab() {
  const [messages, setMessages] = useState([]);
  const load = useCallback(() => api.get("/admin/messages").then(({ data }) => setMessages(Array.isArray(data) ? data : [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="admin-tab-messages">
      {messages.length === 0 && <p className="text-slate-500 text-sm py-10 text-center">Aucun message pour le moment.</p>}
      {messages.map((m) => (
        <div key={m.id} className={`gold-card p-6 ${m.lu ? "opacity-60" : ""}`} data-testid={`message-${m.id}`}>
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <div>
              <p className="font-serif text-xl text-[#F8F9FA]">{m.nom}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mt-1">{m.type_projet}</p>
            </div>
            <div className="flex gap-2">
              {!m.lu && (
                <button onClick={async () => { await api.patch(`/admin/messages/${m.id}/read`); load(); }}
                        data-testid={`mark-read-${m.id}`} className="text-[#D4AF37] hover:text-[#F3E5AB] p-2" aria-label="Marquer comme lu">
                  <MailOpen size={16} />
                </button>
              )}
              <button onClick={async () => { await api.delete(`/admin/messages/${m.id}`); load(); }}
                      data-testid={`delete-message-${m.id}`} className="text-slate-500 hover:text-red-400 p-2" aria-label="Supprimer">
                <Trash2 size={16} />
              </button>
            </div>
          </div>
          <p className="text-slate-300 text-sm leading-relaxed mt-4">{m.message}</p>
          <p className="text-slate-500 text-xs mt-4">{m.courriel}{m.telephone ? ` • ${m.telephone}` : ""} • {new Date(m.created_at).toLocaleDateString("fr-CA")}</p>
        </div>
      ))}
    </div>
  );
}

function ContentTab() {
  const [content, setContent] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/content").then(({ data }) => setContent(data)).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/admin/content", { data: content });
      toast.success("Contenu enregistré. Rechargez le site pour voir les changements.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="gold-card p-8 space-y-5" data-testid="admin-tab-content">
      <p className="text-slate-400 text-sm">Remplacez les photos temporaires par vos vraies photos (collez l'URL de l'image) et ajoutez vos liens.</p>
      {CONTENT_FIELDS.map((f) => (
        <div key={f.key}>
          <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] block mb-2">{f.label}</label>
          <input value={content[f.key] || ""} onChange={(e) => setContent({ ...content, [f.key]: e.target.value })}
                 data-testid={`content-field-${f.key}`} className={inputClass} placeholder="https://…" />
        </div>
      ))}
      <button type="submit" disabled={busy} data-testid="content-save-button" className="btn-gold !px-6 !py-3 disabled:opacity-50">
        {busy ? "Enregistrement…" : "Enregistrer le contenu"}
      </button>
    </form>
  );
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("Messages");

  useEffect(() => {
    if (!loading && !user) navigate("/admin", { replace: true });
  }, [user, loading, navigate]);

  if (loading || !user) return null;

  return (
    <main data-testid="admin-dashboard" className="pt-28 pb-24 min-h-screen max-w-5xl mx-auto px-6">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#F8F9FA]">Tableau de bord</h1>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mt-2">{user.email}</p>
        </div>
        <button onClick={async () => { await logout(); navigate("/admin"); }} data-testid="admin-logout-button" className="btn-outline-gold !px-6 !py-3">
          <LogOut size={14} /> Déconnexion
        </button>
      </div>

      <div className="flex flex-wrap gap-3 mb-10" data-testid="admin-tabs">
        {TABS.map((t) => (
          <button key={t} data-testid={`admin-tab-button-${t.toLowerCase()}`} onClick={() => setTab(t)}
                  className={`text-[11px] uppercase tracking-[0.2em] px-5 py-3 border transition-all duration-300 ${
                    tab === t ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-slate-700 text-slate-400 hover:border-[#D4AF37]/50"
                  }`}>
            {t}
          </button>
        ))}
      </div>

      {tab === "Messages" && <MessagesTab />}
      {tab === "Contenu" ? <ContentTab /> : tab !== "Messages" && <CollectionTab key={tab} tab={tab} />}
    </main>
  );
}
